<template>
  <div class="modal">
    <form class="selection-form">
      <button
        class="cancel-button"
        :aria-label="$t('accountSelector.cancel_button_label').toString()"
        v-on:click="cancel">
        <BackstageIcon class="close-icon" name="close" data-cy="account-selector-popup-close-button"></BackstageIcon>
      </button>

      <h2 class="heading1">
        {{ $t('accountSelector.heading1', { username }) }}
      </h2>
      <p class="heading2">
        {{ $t('accountSelector.heading2') }}
      </p>
      <p class="heading3">
        {{ $t('accountSelector.heading3') }}
      </p>

      <div class="select-and-button">
        <select
          id="account-selector"
          v-model="selectedAccountId"
          data-hj-suppress
          data-cy="account-selector"
          class="with-arrow account-selector">
          <option v-for="account in accounts" :key="account.id" :value="account.id">
            {{ account.name }}
          </option>
        </select>

        <BackstageButton data-cy="confirm-button-cnt" data-cy-button="confirm-button" v-on:click="confirm">
          {{ $t('accountSelector.confirm_button_label') }}
        </BackstageButton>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import BackstageButton from '../../components/common/BackstageButton.vue';
import BackstageIcon from '../../components/common/BackstageIcon.vue';
import { useUserStore } from '../../stores/user';

export default defineComponent({
  name: 'account-selector-modal',
  components: {
    BackstageButton: BackstageButton,
    BackstageIcon: BackstageIcon,
  },
  setup() {
    const { userProfile, selectedAccountId } = useUserStore();

    return {
      accounts: userProfile?.accounts,
      selectedAccountId: selectedAccountId,
      userProfile,
    };
  },
  computed: {
    username(): string | undefined {
      return this.userProfile?.user.username;
    },
  },
  created() {
    if (this.accounts?.length === 1) {
      this.$emit('confirm', this.accounts[0].id);
    }
  },
  methods: {
    cancel(event: Event) {
      event.preventDefault();
      this.$emit('cancel');
    },
    confirm(event: Event) {
      event.preventDefault();
      this.$emit('confirm', this.selectedAccountId);
    },
  },
});
</script>

<style lang="scss" scoped>
@mixin button-reset {
  appearance: none;
  border: none;
  background: none;
}

.modal {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 95;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(50px);
}

.selection-form {
  display: flex;
  flex-direction: column;

  width: 100%;
  max-width: 800px;
  padding: 20px 20px 30px;
  position: relative;
  border-radius: 8px;

  background-color: var(--primary-bg-color);
  color: var(--primary-text-remark-color);
  text-align: center;
}

.heading1 {
  font-size: 1.25rem;
  font-weight: normal;

  border-bottom: 1px var(--border-color) solid;
  padding-bottom: 1rem;
}

.heading2 {
  font-size: 1.15rem;
  margin: 10px 0 0 0;
}

.heading3 {
  margin: 0;
  font-size: 0.9em;
}

.cancel-button {
  @include button-reset;
  cursor: pointer;
  position: absolute;
  top: 1rem;
  right: 1rem;

  .close-icon {
    padding: 0;
    border: none;
    position: fixed;
    width: 42px;
    height: 42px;
    top: 16px;
    right: 16px;
    background-color: var(--primary-bg-color);
    border-radius: 50%;
    opacity: 1;
  }
}

.confirm-button {
  padding: 10px 30px;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
}

.select-and-button {
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: center;
  margin: 35px auto 20px;
  max-width: 100%;
  width: 600px;

  .account-selector {
    max-width: 65%;
    flex: 1;
  }
}

.select-wrapper {
  flex-grow: 1;
  position: relative;

  &::before {
    content: '\e600';
    font-family: icomoon;
    stroke-width: 0;
    stroke: currentColor;
    fill: currentColor;
    color: var(--primary-text-color);
    transform: rotate(90deg);
    pointer-events: none;

    top: 12px;
    right: 10px;
    position: absolute;
    display: block;
  }
}
</style>
